/**
 * "Top up AI" (TMMAPP-0113 §C 7; undrawn, built from the prototype's 460 form dialog): Amount —
 * the default and the minimum are the recharge amount — with "{amount} plus applicable tax"
 * (TMMAPP-0119 item 9, MM-D-139: Stripe Tax calculates it on the charge), then Cancel / TOP UP →
 * `POST /v1/me/ai-wallet/top-up`. A failure stays in the dialog with its action; success and a
 * pending charge close it, and the host shows the answer. A `403 SUBSCRIPTION_REQUIRED` swaps the
 * body for the subscribe card (DLG-005).
 *
 * `TopUpPanel` is the same form as a modal's whole body (TMMAPP-0118 C11, DLG-005): inside a modal,
 * the wallet prompt swaps the host's title, content and actions for it instead of opening a dialog
 * over its host (`useTopUpSwap`).
 *
 * Both name the seller under the title (TMMAPP-0119 §A3).
 *
 * Both keep the old dialog's guards (TMMAPP-0119 item 16): while the charge or the in-place card
 * update is in flight, Cancel, Esc and the backdrop are held; an edited amount asks "Discard this
 * top-up?" before Cancel or Esc throws it away.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type Ref } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DialogGuardBody, type DiscardCopy } from '@/components/DialogGuardBody';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { SellerLine } from '@/features/billing/SellerLine';
import { formatMoneyMinor, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { ChargeOutcomeNotice } from './ChargeOutcomeNotice';
import { chargePreview } from './chargePreview';
import type { AiWalletChargeOutcome } from './useAiWalletCharge';

interface TopUpDialogProps {
  currency: string;
  /** The terms' recharge amount: the default and the smallest top-up. */
  minimumMinor: number;
  outcome: AiWalletChargeOutcome;
  onTopUp: (amountMinor: number) => void;
  onRefresh: () => void;
  onClose: () => void;
  /**
   * Closes this dialog and the modal behind it, so the subscribe card's Subscribe never stacks
   * (DLG-005, TMMAPP-0117 B6); without it, only this dialog closes.
   */
  onCloseHost?: (() => void) | undefined;
}

/** "Discard this top-up?": the discard question in the top-up's own words. */
function useTopUpDiscardCopy(): DiscardCopy {
  const { t } = useTranslation();
  return {
    title: t('aiWallet.discardTitle'),
    consequence: t('aiWallet.discardConsequence'),
    confirmLabel: t('aiWallet.discardConfirm'),
  };
}

/** The amount, its validation and the preview, shared by the dialog and the in-place panel. */
function useTopUpAmount(currency: string, minimumMinor: number) {
  const { t } = useTranslation();
  const locale = useLocale();
  const initial = minorToInputText(minimumMinor, currency, locale);
  const [text, setText] = useState(initial);
  const [touched, setTouched] = useState(false);
  const amountMinor = parseMoneyToMinor(text, currency, locale);
  const minimum = formatMoneyMinor(minimumMinor, currency, locale);
  const invalid = amountMinor === null || amountMinor < minimumMinor;
  const preview = invalid ? null : chargePreview(t, amountMinor, currency, locale);
  return { initial, text, setText, touched, setTouched, amountMinor, minimum, invalid, preview };
}

function TopUpFields({
  amount,
  outcome,
  onRefresh,
  onCloseHost,
  inputRef,
  onCardBusyChange,
}: {
  amount: ReturnType<typeof useTopUpAmount>;
  outcome: AiWalletChargeOutcome;
  onRefresh: () => void;
  onCloseHost: () => void;
  inputRef?: Ref<HTMLInputElement>;
  onCardBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const { text, setText, touched, setTouched, invalid, minimum, preview } = amount;
  if (outcome.kind === 'subscriptionRequired') {
    return (
      <ChargeOutcomeNotice outcome={outcome} onRefresh={onRefresh} onCloseHost={onCloseHost} />
    );
  }
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label={t('aiWallet.amount')}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => setTouched(true)}
        required
        fullWidth
        error={touched && invalid}
        helperText={
          touched && invalid
            ? t('aiWallet.amountMin', { amount: minimum })
            : t('aiWallet.amountHelp', { amount: minimum })
        }
        inputRef={inputRef}
        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      />
      {preview !== null && (
        <Typography variant="body2" data-charge-preview="">
          {preview}
        </Typography>
      )}
      <ChargeOutcomeNotice
        outcome={outcome}
        onRefresh={onRefresh}
        onCloseHost={onCloseHost}
        onBusyChange={onCardBusyChange}
      />
    </Stack>
  );
}

export function TopUpDialog({
  currency,
  minimumMinor,
  outcome,
  onTopUp,
  onRefresh,
  onClose,
  onCloseHost,
}: TopUpDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const fullScreen = useDialogFullScreen();
  const amount = useTopUpAmount(currency, minimumMinor);
  const discardCopy = useTopUpDiscardCopy();
  // The tax-location card update in place (item 11) holds the close as the charge does.
  const [cardUpdating, setCardUpdating] = useState(false);
  const busy = outcome.kind === 'busy' || cardUpdating;
  const guard = useDialogDirtyGuard(amount.text !== amount.initial, onClose, busy);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    amount.setTouched(true);
    if (amount.invalid || amount.amountMinor === null || busy) return;
    onTopUp(amount.amountMinor);
  }

  return (
    <GuardedDialog
      guard={guard}
      discardCopy={discardCopy}
      titleId={titleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xs"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 460 } }}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id={titleId}>{t('aiWallet.topUpTitle')}</DialogTitle>
        <SellerLine dialog />
        <DialogContent>
          <TopUpFields
            amount={amount}
            outcome={outcome}
            onRefresh={onRefresh}
            onCloseHost={onCloseHost ?? onClose}
            onCardBusyChange={setCardUpdating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          {outcome.kind !== 'subscriptionRequired' && (
            <SubmitButton type="submit" loading={outcome.kind === 'busy'} disabled={cardUpdating}>
              {t('aiWallet.topUp')}
            </SubmitButton>
          )}
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}

/**
 * The top-up as its host modal's body (DLG-005: never a dialog over a dialog): its own title (the
 * host's `aria-labelledby` points at `titleId` while it shows), the fields, and Cancel / TOP UP.
 * The Amount field takes focus when it opens; Cancel, and an answer the host shows (an added or
 * pending charge), hand back to the prompt, which restores focus.
 *
 * The host's close (backdrop, Esc) is this panel's Cancel while it shows (`onCloseRequest`, item
 * 16): held in flight, and an edited amount asks "Discard this top-up?" in place first.
 */
export function TopUpPanel({
  currency,
  minimumMinor,
  outcome,
  onTopUp,
  onRefresh,
  onClose,
  onCloseHost,
  titleId,
  onCloseRequest,
}: Omit<TopUpDialogProps, 'onCloseHost'> & {
  onCloseHost: () => void;
  titleId: string;
  /** Registers the panel's close request with its host (`TopUpSwap.onCloseRequest`). */
  onCloseRequest: (request: (() => void) | null) => void;
}) {
  const { t } = useTranslation();
  const amount = useTopUpAmount(currency, minimumMinor);
  const discardCopy = useTopUpDiscardCopy();
  const [cardUpdating, setCardUpdating] = useState(false);
  const busy = outcome.kind === 'busy' || cardUpdating;
  const guard = useDialogDirtyGuard(amount.text !== amount.initial, onClose, busy);
  const { requestClose } = guard;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    onCloseRequest(requestClose);
    return () => onCloseRequest(null);
  }, [onCloseRequest, requestClose]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    amount.setTouched(true);
    if (amount.invalid || amount.amountMinor === null || busy) return;
    onTopUp(amount.amountMinor);
  }

  return (
    // Esc is the panel's Cancel, before the host dialog can treat it as "close" (DiscardPanel's
    // rule); on the discard question it is Keep editing.
    <Box
      sx={{ display: 'contents' }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        requestClose();
      }}
    >
      {/* While the question shows it takes the swap's title id, so the host dialog is named by it. */}
      <DialogGuardBody guard={guard} copy={discardCopy} titleId={titleId}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          aria-labelledby={guard.discarding ? undefined : titleId}
          data-top-up-panel=""
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {/* An explicit id either way: MUI's DialogTitle falls back to the host's labelledby id. */}
          <DialogTitle id={guard.discarding ? `${titleId}-form` : titleId}>
            {t('aiWallet.topUpTitle')}
          </DialogTitle>
          <SellerLine dialog />
          <DialogContent>
            <TopUpFields
              amount={amount}
              outcome={outcome}
              onRefresh={onRefresh}
              onCloseHost={onCloseHost}
              inputRef={inputRef}
              onCardBusyChange={setCardUpdating}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={requestClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            {outcome.kind !== 'subscriptionRequired' && (
              <SubmitButton type="submit" loading={outcome.kind === 'busy'} disabled={cardUpdating}>
                {t('aiWallet.topUp')}
              </SubmitButton>
            )}
          </DialogActions>
        </Box>
      </DialogGuardBody>
    </Box>
  );
}
